import { LuArrowRight, LuCirclePlus, LuX } from 'react-icons/lu'

import { useGetUser } from '@/feature/auth'
import { EtaLocation, EtaLocationType, useEtaConfig } from '@/feature/eta'
import { useFlag } from '@/feature/feature-flags'
import { Route } from '@/feature/map'
import { useCreateTaskModalStore } from '@/feature/execution-board/components/CreateTaskModal/createTaskModalStore'
import { usePersistentObservatoryPageStore } from '@/feature/observatory'

import { CopyButton } from '@/lib/core/components/CopyButton'

import { useEtaCalculation } from '../../hooks/useEtaCalculation'
import { useEtaCopyToClipboard } from '../../hooks/useEtaCopyToClipboard'
import { useEtaMapInitialization } from '../../hooks/useEtaMapInitialization'
import { useEtaToTask } from '../../hooks/useEtaToTask'
import styles from './Eta.module.css'
import { EtaMapMarker } from './EtaMapMarker'
import { AssetLocationFrom, CustomLocationFrom, GeofenceLocationFrom } from './LocationFrom'
import { AssetLocationTo, CustomLocationTo, GeoFenceLocationTo } from './LocationTo'

// Re-export sub-components for backwards compatibility
export { EtaMapMarker } from './EtaMapMarker'
export { AssetLocationFrom, GeofenceLocationFrom } from './LocationFrom'
export { AssetLocationTo } from './LocationTo'
export { LocationToContent } from './LocationToContent'

export const Eta = ({
  etaLocationFrom,
  etaLocationTo,
  onClose,
}: {
  etaLocationFrom: EtaLocation
  etaLocationTo: EtaLocation
  onClose: () => void
}) => {
  const toggleAssets = usePersistentObservatoryPageStore((state) => state.toggles.setAssets)
  const toggleGeoFences = usePersistentObservatoryPageStore((state) => state.toggles.setGeoFences)

  const routeLayerConfig = useEtaConfig()
  const applyFudgeFactor = useFlag<boolean>('manualEtaTest', false)

  const { data: currentUser } = useGetUser()

  // Hide assets/geofences while the ETA panel is open
  useEtaMapInitialization()

  // Calculate ETA
  const { etaResult, etaError } = useEtaCalculation({
    etaLocationFrom,
    etaLocationTo,
    applyFudgeFactor,
    routeLayerConfig,
    organizationName: currentUser?.organization?.name,
  })

  const getCopyText = useEtaCopyToClipboard({
    etaLocationFrom,
    etaLocationTo,
    etaResult,
  })

  // ETA to Task conversion
  const isExecutionBoardEnabled = useFlag<boolean>('featureExecutionBoardUi', false)
  const buildTaskData = useEtaToTask({ etaLocationFrom, etaLocationTo, etaResult })
  const openCreateTaskModal = useCreateTaskModalStore((state) => state.openModal)

  const handleClose = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    toggleAssets(true)
    toggleGeoFences(true)
    onClose()
  }

  return (
    <>
      {etaResult && (
        <>
          <Route layers={routeLayerConfig.route.layers} route={etaResult?.overviewPath} />
          <EtaMapMarker etaLocation={etaLocationTo} />
          <EtaMapMarker etaLocation={etaLocationFrom} />
        </>
      )}

      <div className={styles.mainWindow}>
        <div className={styles.headerBar}>
          <div className={styles.title}>
            ETA for{' '}
            <span className={styles.locationName}>
              {etaLocationFrom?.type === EtaLocationType.ASSET
                ? etaLocationFrom?.plateNumber
                  ? `Asset ${etaLocationFrom.title} (${etaLocationFrom.plateNumber})`
                  : `Asset ${etaLocationFrom.title}`
                : etaLocationFrom.title}
            </span>
            {etaLocationFrom?.title ? ' to ' : ''}
            <span className={styles.locationName}>
              {etaLocationTo?.type === EtaLocationType.ASSET
                ? etaLocationTo?.plateNumber
                  ? `Asset ${etaLocationTo.title} (${etaLocationTo.plateNumber})`
                  : `Asset ${etaLocationTo.title}`
                : etaLocationTo.title}
            </span>
          </div>
          <div className={styles.actions}>
            {isExecutionBoardEnabled && buildTaskData && (
              <button
                className={`${styles.actionButton} ${styles.createTaskAction}`}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  openCreateTaskModal(buildTaskData?.())
                }}
                data-testid="eta-create-task-button"
              >
                <LuCirclePlus size={16} /> Task
              </button>
            )}
            <CopyButton getText={getCopyText} className={styles.actionButton} size={16} />
            <button className={styles.closeAction} onClick={handleClose} data-testid="eta-close-button">
              <LuX size={16} />
            </button>
          </div>
        </div>
        <div className={styles.content}>
          <div className={styles.locationBlock}>
            {etaLocationFrom?.type === EtaLocationType.GEOFENCE && (
              <GeofenceLocationFrom etaLocation={etaLocationFrom} />
            )}
            {etaLocationFrom?.type === EtaLocationType.ASSET && (
              <AssetLocationFrom etaLocation={etaLocationFrom} />
            )}
            {etaLocationFrom?.type === EtaLocationType.CUSTOM && (
              <CustomLocationFrom etaLocation={etaLocationFrom} />
            )}
          </div>
          <div className={styles.arrowLeft}>
            <LuArrowRight />
          </div>

          <div className={styles.locationBlock}>
            {etaLocationTo?.type === EtaLocationType.GEOFENCE && (
              <GeoFenceLocationTo
                etaLocation={etaLocationTo}
                etaResult={etaResult}
                error={etaError}
              />
            )}
            {etaLocationTo?.type === EtaLocationType.ASSET && (
              <AssetLocationTo etaLocation={etaLocationTo} etaResult={etaResult} error={etaError} />
            )}
            {etaLocationTo?.type === EtaLocationType.CUSTOM && (
              <CustomLocationTo
                etaLocation={etaLocationTo}
                etaResult={etaResult}
                error={etaError}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}
